import { STRINGS } from 'config';
import { NotificationModel } from 'pages/Consultation';
import { useEffect, useRef, useState } from 'react';
import { getTranscriptionToken } from 'store/action/transcriptiontoken';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { reportError } from 'util/datadog';
import { RecordingStatus, TranscriberStatus } from './LiveTranscription';
import { useAudioProcessor } from './useAudioProcessor';
import { useWebSocket } from './useWebSocket';

const {
  SUMMARISER: { WEBSOCKET },
  TRANSCRIBE: { SAMPLE_RATE_HERTZ },
  REDUCERS: {
    WEBSOCKET: { MESSAGE_ERROR },
    TRANSCRIPTION: { ADD_TRANSCRIPTION },
  },
} = STRINGS;

export const useLiveTranscription = (
  consultationId: string,
  setNotification: React.Dispatch<React.SetStateAction<NotificationModel | null>>,
) => {
  const dispatch = useAppDispatch();
  const [recordingState, setRecording] = useState<RecordingStatus>(RecordingStatus.READY);
  const [interim, setInterimTranscription] = useState<string | null>(null);
  const [transcriberState, setTranscriberState] = useState<TranscriberStatus>(TranscriberStatus.READY);
  const { app } = useAppState();

  const { connect, send, closeConnection } = useWebSocket();
  const { startAudio, stopAudio, bufferSize } = useAudioProcessor(SAMPLE_RATE_HERTZ, setNotification, setRecording);

  const finalizePromiseRef = useRef<(() => void) | null>(null);

  const stopRecording = async (state: RecordingStatus) => {
    stopAudio();
    setRecording(state);
    setTranscriberState(TranscriberStatus.CLOSING);

    return new Promise<void>(resolve => {
      finalizePromiseRef.current = resolve;
    });
  };

  const startRecording = async () => {
    setRecording(RecordingStatus.LOADING);
    const jwtToken = await getTranscriptionToken(app?.selectedPractice?.id || '');
    const url = new URL(WEBSOCKET);
    url.searchParams.append('jwt', jwtToken);
    url.searchParams.append('consultationId', consultationId);
    connect(
      url,
      data => {
        const { error } = data;
        if (error) {
          if (error === 'quota_exceeded') {
            // WebSocket connection closes from backend closing with 0 freeBalanceSeconds
            return stopRecording(RecordingStatus.PAUSED);
          }
          dispatch({
            type: MESSAGE_ERROR,
            payload: error,
          });
          reportError(new Error('Error with messages in transcription'), { consultationId, error });
          setNotification({
            textContent:
              'There has been an error. If the issue persists please contact us at practice.support@econsult.health.',
            state: 'warning',
          });
        } else {
          const { transcription, isFinal } = data;
          if (isFinal) {
            dispatch({
              type: ADD_TRANSCRIPTION,
              payload: transcription,
            });
            setInterimTranscription(null);
          } else {
            setInterimTranscription(transcription);
          }
        }
        return () => {};
      },
      () => {
        setRecording(RecordingStatus.ERROR);
        setNotification({ textContent: 'Connection error', state: 'urgent' });
      },
    );

    await startAudio(audioBuffer => {
      send(audioBuffer.buffer);
    });

    setRecording(RecordingStatus.RECORDING);
    setTranscriberState(TranscriberStatus.TRANSCRIBING);
  };

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    if (transcriberState === TranscriberStatus.CLOSING) {
      if (interim) {
        const silenceSendInterval = (bufferSize / SAMPLE_RATE_HERTZ) * 1000;
        const silence = new Int16Array(bufferSize);

        intervalId = setInterval(() => {
          send(silence.buffer);
        }, silenceSendInterval);
      }

      if (!interim) {
        closeConnection();
        setTranscriberState(TranscriberStatus.READY_TO_SUMMARISE);
        if (finalizePromiseRef.current) {
          finalizePromiseRef.current();
          finalizePromiseRef.current = null;
        }
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [transcriberState, interim, send, closeConnection, bufferSize]);

  return {
    recordingState,
    interim,
    startRecording,
    stopRecording,
    transcriberState,
  };
};
